import type { TemplateProps } from '@/types/template';
import { cvLabel, languageLevelLabel, skillLevelLabel } from '@/i18n/cvLabels';
import { formatDateRange } from '@/utils/date';
import { fullName, isSectionEmpty, orderedSections } from '@/templates/shared/utils';
import { RichTextView } from '@/components/common/RichTextView';
import type { LanguageLevel, ModernSidebarStyle, SkillLevel } from '@/types/cv';

const SKILL_RANK: Record<SkillLevel, number> = {
  beginner: 1,
  intermediate: 2,
  advanced: 3,
  expert: 4,
};
const SKILL_MAX = 4;

const LANGUAGE_RANK: Record<LanguageLevel, number> = {
  A1: 1,
  A2: 2,
  B1: 3,
  B2: 4,
  C1: 5,
  C2: 6,
  native: 6,
};
const LANGUAGE_MAX = 6;

export function ModernHtml({ data, customColor, photoUrl }: TemplateProps) {
  const lang = data.settings.language;
  const accent = customColor ?? data.settings.color ?? '#0f766e';
  const sections = orderedSections(data);
  const present = cvLabel(lang, 'present');
  const sidebarStyle: ModernSidebarStyle = data.settings.modernSidebarStyle ?? 'dots';
  const sidebarSections: typeof sections = [];
  const mainSections: typeof sections = [];
  for (const s of sections) {
    if (s === 'skills' || s === 'languages' || s === 'certifications') sidebarSections.push(s);
    else mainSections.push(s);
  }

  return (
    <article
      className="mx-auto bg-white text-[10pt] leading-snug text-slate-900"
      style={{
        width: '210mm',
        minHeight: '297mm',
        fontFamily: 'Inter, Helvetica, Arial, sans-serif',
      }}
    >
      <div className="grid" style={{ gridTemplateColumns: '70mm 1fr' }}>
        <aside className="px-5 py-6 text-white" style={{ background: accent, minHeight: '297mm' }}>
          {photoUrl && (
            <img
              src={photoUrl}
              alt=""
              className="mx-auto h-32 w-32 rounded-full object-cover ring-2 ring-white/40"
            />
          )}
          <h1 className="mt-4 text-2xl font-bold leading-tight">{fullName(data) || '—'}</h1>
          {data.personalInfo.title && (
            <p className="mt-1 text-sm opacity-90">{data.personalInfo.title}</p>
          )}

          <SidebarSection title={cvLabel(lang, 'contact')}>
            <ContactList data={data} />
          </SidebarSection>

          {sidebarSections.includes('skills') && !isSectionEmpty(data, 'skills') && (
            <SidebarSection title={cvLabel(lang, 'skills')}>
              <SkillsList data={data} lang={lang} style={sidebarStyle} />
            </SidebarSection>
          )}

          {sidebarSections.includes('languages') && !isSectionEmpty(data, 'languages') && (
            <SidebarSection title={cvLabel(lang, 'languages')}>
              <LanguagesList data={data} lang={lang} style={sidebarStyle} />
            </SidebarSection>
          )}

          {sidebarSections.includes('certifications') &&
            !isSectionEmpty(data, 'certifications') && (
              <SidebarSection title={cvLabel(lang, 'certifications')}>
                <ul className="space-y-1 text-xs">
                  {data.certifications?.map((c) => (
                    <li key={c.id}>
                      <span className="block font-medium">{c.name}</span>
                      <span className="opacity-90">
                        {c.issuer} · {c.date}
                      </span>
                    </li>
                  ))}
                </ul>
              </SidebarSection>
            )}
        </aside>

        <main className="px-7 py-6">
          {data.personalInfo.summary && (
            <MainSection title={cvLabel(lang, 'summary')} accent={accent}>
              <RichTextView markdown={data.personalInfo.summary} />
            </MainSection>
          )}
          {mainSections.map((key) => {
            if (isSectionEmpty(data, key)) return null;
            switch (key) {
              case 'experience':
                return (
                  <MainSection key={key} title={cvLabel(lang, 'experience')} accent={accent}>
                    {data.experience.map((e) => (
                      <div key={e.id} className="mb-3">
                        <div className="flex items-baseline justify-between">
                          <p className="font-semibold">{e.position}</p>
                          <span className="text-xs text-slate-500">
                            {formatDateRange(e.startDate, e.endDate, {
                              presentLabel: present,
                              locale: lang,
                            })}
                          </span>
                        </div>
                        <p className="text-sm" style={{ color: accent }}>
                          {e.company}
                          {e.location && <span className="text-slate-500"> · {e.location}</span>}
                        </p>
                        {e.description && (
                          <div className="mt-1">
                            <RichTextView markdown={e.description} />
                          </div>
                        )}
                        {e.achievements && e.achievements.length > 0 && (
                          <ul className="mt-1 list-disc pl-5">
                            {e.achievements.map((a, i) => (
                              <li key={i}>
                                <RichTextView
                                  markdown={a}
                                  paragraphClassName="m-0"
                                  listClassName="list-[circle] pl-5"
                                />
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ))}
                  </MainSection>
                );
              case 'education':
                return (
                  <MainSection key={key} title={cvLabel(lang, 'education')} accent={accent}>
                    {data.education.map((e) => (
                      <div key={e.id} className="mb-2">
                        <div className="flex items-baseline justify-between">
                          <p className="font-semibold">
                            {e.degree}
                            {e.field && <span className="font-normal"> · {e.field}</span>}
                          </p>
                          <span className="text-xs text-slate-500">
                            {formatDateRange(e.startDate, e.endDate, {
                              presentLabel: present,
                              locale: lang,
                            })}
                          </span>
                        </div>
                        <p className="text-sm" style={{ color: accent }}>
                          {e.institution}
                        </p>
                        {e.description && (
                          <div className="mt-1">
                            <RichTextView markdown={e.description} />
                          </div>
                        )}
                      </div>
                    ))}
                  </MainSection>
                );
              case 'projects':
                return (
                  <MainSection key={key} title={cvLabel(lang, 'projects')} accent={accent}>
                    {data.projects?.map((p) => (
                      <div key={p.id} className="mb-2">
                        <p className="font-semibold">{p.name}</p>
                        {p.technologies && p.technologies.length > 0 && (
                          <p className="text-xs italic" style={{ color: accent }}>
                            {p.technologies.join(' · ')}
                          </p>
                        )}
                        {p.description && <RichTextView markdown={p.description} />}
                        {p.url && <p className="text-xs text-slate-500">{p.url}</p>}
                      </div>
                    ))}
                  </MainSection>
                );
              case 'customSections':
                return (
                  <div key={key}>
                    {data.customSections?.map((c) => (
                      <MainSection key={c.id} title={c.title} accent={accent}>
                        <RichTextView markdown={c.content} />
                      </MainSection>
                    ))}
                  </div>
                );
              default:
                return null;
            }
          })}
        </main>
      </div>
    </article>
  );
}

function SidebarSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-5">
      <h2 className="mb-1 text-xs font-bold uppercase tracking-widest opacity-90">{title}</h2>
      {children}
    </section>
  );
}

function MainSection({
  title,
  accent,
  children,
}: {
  title: string;
  accent: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mt-4">
      <h2
        className="mb-2 text-sm font-bold uppercase tracking-wider"
        style={{ color: accent, borderBottom: `2px solid ${accent}`, paddingBottom: 2 }}
      >
        {title}
      </h2>
      {children}
    </section>
  );
}

function ContactList({ data }: { data: TemplateProps['data'] }) {
  const { email, phone, address, websites } = data.personalInfo;
  return (
    <ul className="space-y-1 text-xs">
      {email && <li>{email}</li>}
      {phone && <li>{phone}</li>}
      {address?.city && <li>{[address.city, address.country].filter(Boolean).join(', ')}</li>}
      {websites?.map((w) => (
        <li key={w.id}>
          <span className="font-semibold">{w.label}:</span>{' '}
          <a href={w.url} className="underline-offset-2 hover:underline">
            {w.displayText || w.url}
          </a>
        </li>
      ))}
    </ul>
  );
}

function SkillsList({
  data,
  lang,
  style,
}: {
  data: TemplateProps['data'];
  lang: TemplateProps['data']['settings']['language'];
  style: ModernSidebarStyle;
}) {
  if (style === 'chips') {
    return (
      <div className="flex flex-wrap gap-1.5">
        {data.skills.map((s) => (
          <span
            key={s.id}
            className="rounded-full bg-white/15 px-2 py-0.5 text-[10px] font-medium leading-tight ring-1 ring-white/25"
            title={s.level ? skillLevelLabel(lang, s.level) : undefined}
          >
            {s.name}
          </span>
        ))}
      </div>
    );
  }

  if (style === 'bars') {
    return (
      <ul className="space-y-1.5">
        {data.skills.map((s) => {
          const rank = s.level ? SKILL_RANK[s.level] : 0;
          const pct = rank ? (rank / SKILL_MAX) * 100 : 0;
          return (
            <li key={s.id} className="text-[10px]">
              <div className="flex items-baseline justify-between gap-2">
                <span className="font-medium">{s.name}</span>
                {s.level && (
                  <span className="text-[9px] opacity-75">{skillLevelLabel(lang, s.level)}</span>
                )}
              </div>
              {rank > 0 && (
                <div className="mt-0.5 h-1 w-full rounded-full bg-white/20">
                  <div className="h-full rounded-full bg-white" style={{ width: `${pct}%` }} />
                </div>
              )}
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <ul className="space-y-1 text-xs">
      {data.skills.map((s) => {
        const rank = s.level ? SKILL_RANK[s.level] : 0;
        return (
          <li key={s.id} className="flex items-center justify-between gap-2">
            <span className="truncate font-medium">{s.name}</span>
            {rank > 0 && <Dots filled={rank} total={SKILL_MAX} />}
          </li>
        );
      })}
    </ul>
  );
}

function LanguagesList({
  data,
  lang,
  style,
}: {
  data: TemplateProps['data'];
  lang: TemplateProps['data']['settings']['language'];
  style: ModernSidebarStyle;
}) {
  if (style === 'chips') {
    return (
      <ul className="space-y-1.5">
        {data.languages.map((l) => {
          const rank = LANGUAGE_RANK[l.level];
          return (
            <li key={l.id}>
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-xs font-medium">{l.name}</span>
                <span className="text-[10px] opacity-80">{languageLevelLabel(lang, l.level)}</span>
              </div>
              <Dots filled={rank} total={LANGUAGE_MAX} size="sm" />
            </li>
          );
        })}
      </ul>
    );
  }

  if (style === 'bars') {
    return (
      <ul className="space-y-1.5">
        {data.languages.map((l) => {
          const rank = LANGUAGE_RANK[l.level];
          const pct = (rank / LANGUAGE_MAX) * 100;
          return (
            <li key={l.id} className="text-[10px]">
              <div className="flex items-baseline justify-between gap-2">
                <span className="font-medium">{l.name}</span>
                <span className="text-[9px] opacity-75">{languageLevelLabel(lang, l.level)}</span>
              </div>
              <div className="mt-0.5 h-1 w-full rounded-full bg-white/20">
                <div className="h-full rounded-full bg-white" style={{ width: `${pct}%` }} />
              </div>
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <ul className="space-y-1 text-xs">
      {data.languages.map((l) => {
        const rank = LANGUAGE_RANK[l.level];
        return (
          <li key={l.id} className="flex items-center justify-between gap-2">
            <span className="truncate font-medium">{l.name}</span>
            <Dots filled={rank} total={LANGUAGE_MAX} />
          </li>
        );
      })}
    </ul>
  );
}

function Dots({
  filled,
  total,
  size = 'md',
}: {
  filled: number;
  total: number;
  size?: 'sm' | 'md';
}) {
  const dotClass = size === 'sm' ? 'h-1.5 w-1.5' : 'h-2 w-2';
  return (
    <span className="flex shrink-0 items-center gap-0.5" aria-hidden>
      {Array.from({ length: total }).map((_, i) => (
        <span
          key={i}
          className={`${dotClass} rounded-full ${i < filled ? 'bg-white' : 'bg-white/30'}`}
        />
      ))}
    </span>
  );
}
